import { motion } from "framer-motion";
import { ArrowUpRight, Check } from "lucide-react";
import { EASE } from "../../lib/site";
import { Reveal } from "./Reveal";

const fade = (delay) => ({
  initial: { opacity: 0 },
  whileInView: { opacity: 1 },
  viewport: { once: true, amount: 0.15 },
  transition: { duration: 0.9, ease: EASE, delay },
});

/* Rounded corners on a wrapper that exactly matches the image's own size:
   the img is display:block w-full h-auto, so nothing is ever clipped. */
const FullImage = ({ src, alt, testid, delay, className = "" }) => (
  <div className={`overflow-hidden rounded-[28px] ${className}`}>
    <motion.img
      {...fade(delay)}
      src={src}
      alt={alt}
      loading="lazy"
      data-testid={testid}
      className="block h-auto w-full object-contain"
    />
  </div>
);

export const FeaturedProject = ({ p, index, images }) => {
  const t = (m, base) => `featured-project-${index}${m ? "-m" : ""}-${base}`;

  /* Shared text blocks; m=true marks the mobile-only copies so test ids stay unique. */
  const textBlocks = (m) => (
    <>
      <Reveal delay={0.15}>
        <p className="text-base leading-relaxed text-body-ink">{p.summary}</p>
      </Reveal>
      <Reveal delay={0.2}>
        <ul className="space-y-3">
          {p.points.map((x) => (
            <li key={x} className="flex gap-3 text-[16px] leading-relaxed text-ink/85 min-[900px]:text-sm">
              <Check className="mt-1 h-4 w-4 shrink-0 text-terra min-[900px]:mt-0.5" /> {x}
            </li>
          ))}
        </ul>
      </Reveal>
      <Reveal delay={0.25} className="mt-8 grid grid-cols-3 divide-x divide-ink/10 rounded-2xl border border-ink/10 bg-white">
        {p.metrics.map((metric, i) => (
          <div key={metric.l} data-testid={t(m, `metric-${i}`)} className="p-4">
            <div className="font-serif text-3xl text-ink">{metric.v}</div>
            <div className="mt-1 text-xs leading-snug text-muted-ink">{metric.l}</div>
          </div>
        ))}
      </Reveal>
      {p.link && (
        <Reveal delay={0.3} className="mt-6">
          <a href={p.link} target="_blank" rel="noopener noreferrer" data-testid={t(m, "shop-link")} className="btn-outline inline-flex items-center gap-2 !py-2.5 !text-sm">
            Visit the Etsy shop <ArrowUpRight className="h-4 w-4" />
          </a>
        </Reveal>
      )}
    </>
  );

  /* Natural-ratio images, never cropped. collage=true keeps the 2-column
     desktop arrangement with the last odd photo spanning full width. */
  const imageStack = (m, collage) => (
    <div
      data-testid={t(m, collage ? "gallery-collage" : m ? "gallery" : "gallery")}
      className={collage ? "grid grid-cols-2 items-start gap-4" : "flex flex-col gap-4"}
    >
      {images.map((src, i) => (
        <FullImage
          key={src}
          src={src}
          alt={`${p.title} - photo ${i + 1}`}
          testid={t(m, `${m ? "image" : "desktop-image"}-${i}`)}
          delay={0.1 + i * 0.08}
          className={collage && images.length % 2 === 1 && i === images.length - 1 ? "col-span-2" : ""}
        />
      ))}
    </div>
  );

  /* Shop preview card: header (avatar + name), product photo at natural ratio, caption. */
  const shopCard = (m) => p.shop && (
    <div data-testid={t(m, "shop-card")} className="mt-4 rounded-[28px] border border-ink/10 bg-white p-5">
      <div className="flex items-center gap-4">
        <div className="h-[72px] w-[72px] shrink-0 overflow-hidden rounded-[12px] border border-ink/10 bg-paper">
          <img src={p.shop.avatar} alt={`${p.shop.name} shop avatar: ink drawing of two hands holding a bouquet`} loading="lazy" className="h-full w-full object-contain" />
        </div>
        <div className="min-w-0">
          <div className="font-serif text-xl leading-tight text-ink">{p.shop.name}</div>
          <div className="mt-0.5 text-xs text-muted-ink">{p.shop.location}</div>
        </div>
      </div>
      <div className="mt-4 overflow-hidden rounded-2xl">
        <motion.img
          {...fade(0.2)}
          src={p.shop.product}
          alt={`${p.shop.caption}, hand-painted and photographed among fresh cream roses`}
          loading="lazy"
          data-testid={t(m, "shop-photo")}
          className="block h-auto w-full object-contain"
        />
      </div>
      <div className="mt-3 text-xs text-muted-ink">{p.shop.caption}</div>
    </div>
  );

  return (
    <article data-testid={`featured-project-${index}`}>
      <Reveal className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
        <span className="flex items-baseline gap-4">
          <span className="font-serif text-7xl italic leading-none text-terra/90">0{index + 1}</span>
          <span className="font-mono text-xs uppercase tracking-[0.24em] text-muted-ink">{p.role} · {p.year}</span>
        </span>
        <span className="rounded-full border border-ink/10 bg-paper/90 px-4 py-1.5 font-mono text-[11px] uppercase tracking-[0.2em] text-ink">{p.tag}</span>
      </Reveal>
      <Reveal delay={0.1}>
        <h3 className="mt-5 font-serif text-3xl leading-tight text-ink sm:text-4xl">{p.title}</h3>
      </Reveal>

      {/* Mobile / tablet: single column, label > title > intro > bullets > stats > images */}
      <div className="mt-8 min-[900px]:hidden">
        <div className="flex flex-col gap-8">{textBlocks(true)}</div>
        <div className="mt-8">{imageStack(true, false)}{shopCard(true)}</div>
      </div>

      {/* Desktop: two columns, text left / images right at natural ratios, scrolling naturally (no sticky, no crop) */}
      <div className="mt-8 hidden min-[900px]:grid min-[900px]:grid-cols-[55fr_45fr] min-[900px]:gap-x-12">
        <div className="min-w-0">
          <div className="flex flex-col gap-8">{textBlocks(false)}</div>
        </div>
        <div>
          {imageStack(false, images.length > 3)}
          {shopCard(false)}
        </div>
      </div>
    </article>
  );
};
